import { forwardRef, useState, useEffect, useCallback, useRef } from 'react';

import {
  TextField,
  InputAdornment,
  SxProps,
  Theme,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { FieldValues, ControllerRenderProps } from 'react-hook-form';
import { IMaskInput } from 'react-imask';

type BaseValidation = Readonly<{
  type: 'number' | 'text' | 'url';
}>;

type NumberValidation = BaseValidation & {
  type: 'number';
  format: 'whole' | 'decimal';
  min?: number;
  max?: number;
  decimals?: number;
};

type TextValidation = BaseValidation & {
  type: 'text';
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
};

type UrlValidation = BaseValidation & {
  type: 'url';
  allowedDomains?: string[];
};

type Validation = NumberValidation | TextValidation | UrlValidation;

// Formats the input URL by ensuring it has a protocol
const formatUrl = (value: string): string => {
  if (!value.startsWith('http://') && !value.startsWith('https://')) {
    return `http://${value}`;
  }

  return value;
};

// Validates the formatted URL using optional regex and URL constructor
const isValidUrl = (value: string, regex?: RegExp) => {
  const inputUrl = formatUrl(value);

  if (regex && !inputUrl.match(regex)) {
    return { isValid: false, url: null };
  }

  try {
    new URL(inputUrl);

    return { url: inputUrl, isValid: true };
  } catch (e) {
    console.error(e);

    return { url: inputUrl, isValid: false };
  }
};

const validateValue = (
  value: string,
  validation?: Validation,
): { isValid: boolean; error?: string } => {
  if (value === '') return { isValid: true };

  if (!validation) return { isValid: true };

  switch (validation.type) {
    case 'number': {
      const num = Number(value);

      if (isNaN(num)) return { isValid: false, error: 'Please enter a valid number' };

      if (
        (validation.min !== undefined && num < validation.min) ||
        (validation.max !== undefined && num > validation.max)
      ) {
        if (validation.min !== undefined && validation.max !== undefined) {
          return {
            isValid: false,
            error: `Value must be between ${validation.min} and ${validation.max}`,
          };
        }

        if (validation.min !== undefined) {
          return { isValid: false, error: `Value must be at least ${validation.min}` };
        }

        if (validation.max !== undefined) {
          return { isValid: false, error: `Value must be at most ${validation.max}` };
        }
      }

      if (validation.format === 'whole' && !Number.isInteger(num)) {
        return { isValid: false, error: 'Please enter a whole number' };
      }
      if (validation.format === 'decimal') {
        const decimals = value.split('.')[1]?.length ?? 0;

        if (decimals > (validation.decimals ?? 2)) {
          return {
            isValid: false,
            error: `Please enter a number with at most ${validation.decimals} decimal places`,
          };
        }
      }

      return { isValid: true };
    }

    case 'text': {
      if (validation.minLength !== undefined && value.length < validation.minLength) {
        return {
          isValid: false,
          error: `Text must be at least ${validation.minLength} characters`,
        };
      }
      if (validation.maxLength !== undefined && value.length > validation.maxLength) {
        return { isValid: false, error: `Text must be at most ${validation.maxLength} characters` };
      }
      if (validation.pattern) {
        const { isValid } = isValidUrl(value, validation.pattern);

        if (!isValid) {
          return { isValid: false, error: 'Please enter a valid value' };
        }
      }

      return { isValid: true };
    }

    case 'url': {
      try {
        const url = new URL(value);

        if (
          validation.allowedDomains &&
          !validation.allowedDomains.some((domain) => url.hostname.endsWith(domain))
        ) {
          return {
            isValid: false,
            error: `URL must be from one of: ${validation.allowedDomains.join(', ')}`,
          };
        }

        return { isValid: true };
      } catch {
        return { isValid: false, error: 'Please enter a valid URL' };
      }
    }

    default:
      return { isValid: true };
  }
};

// we use any here to get the correct match with IMaskInputProps
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const NumberMask = forwardRef<HTMLInputElement, any>(function NumberMask(props, ref) {
  const { onChange, value, validation, ...rest } = props;

  const isDecimal = validation?.format === 'decimal';
  const decimals = validation?.decimals ?? 2;

  return (
    <IMaskInput
      {...rest}
      inputRef={ref}
      value={value?.toString() ?? ''}
      mask={Number}
      scale={isDecimal ? decimals : 0} // number of decimal places
      signed={undefined}
      thousandsSeparator=""
      padFractionalZeros={false}
      normalizeZeros={true}
      radix="."
      mapToRadix={['.']}
      min={validation?.min}
      max={validation?.max}
      unmask={true}
      onAccept={(unmaskedValue: string) => {
        onChange({ target: { value: unmaskedValue } });
      }}
    />
  );
});

type ProfileFormField = Readonly<
  {
    customStartAdornment?: React.ReactNode;
    placeholder?: string;
  } & Omit<TextFieldProps, 'onChange' | 'value'>
>;

type ProfileFormTextFieldProps = Readonly<{
  field: ProfileFormField;
  groupedFields: Record<string, ProfileFormField[]>;
  index: number;
  controllerField: ControllerRenderProps<FieldValues, string>;
  error?: string;
  validation?: Validation;
  sx?: SxProps<Theme>;
  disabled?: boolean;
  useTimeout?: boolean;
}>;

export const ProfileFormTextField = ({
  field,
  groupedFields,
  index,
  error,
  controllerField,
  validation,
  sx,
  disabled,
  useTimeout = true,
}: ProfileFormTextFieldProps) => {
  const [localValue, setLocalValue] = useState(controllerField.value ?? '');
  const [localError, setLocalError] = useState<string | undefined>(error);
  const commitTimeoutRef = useRef<NodeJS.Timeout>();
  const errorTimeoutRef = useRef<NodeJS.Timeout>();

  /**
   * CHANGE
   *
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    setLocalValue(value);
    setLocalError(undefined);
    // Clear any existing timeouts
    clearTimeout(commitTimeoutRef.current);
    clearTimeout(errorTimeoutRef.current);

    // Set a timeout to show validation errors after 1 second of typing
    errorTimeoutRef.current = setTimeout(() => {
      const { error: validationError } = validateValue(value, validation);

      setLocalError(validationError);
    }, 1000);

    if (useTimeout) {
      // Set a new timeout to commit the value after 2s of no changes
      commitTimeoutRef.current = setTimeout(() => {
        const { isValid } = validateValue(value, validation);

        if (isValid) {
          controllerField.onChange({ target: { value } });
        }
      }, 2000);
    }
  };

  /**
   * BLUR
   *
   */
  const handleBlur = useCallback(() => {
    const { isValid } = validateValue(localValue, validation);

    if (isValid) {
      controllerField.onChange({ target: { value: localValue } });

      return;
    }

    setLocalValue(controllerField.value?.toString() ?? '');
  }, [localValue, validation, controllerField]);

  /**
   * UNMOUNT
   *
   */
  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (commitTimeoutRef.current) {
        clearTimeout(commitTimeoutRef.current);
      }
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      {field.label && (
        <Typography variant="text-sm-regular" sx={labelSmallScreenSx}>
          {field.label}
        </Typography>
      )}
      <TextField
        id={field.name}
        name={field.name}
        placeholder={field.placeholder}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={disabled}
        error={!!localError}
        helperText={localError}
        autoComplete={validation?.type === 'number' ? 'off' : 'on'}
        InputProps={{
          ...(validation?.type === 'number' && {
            inputComponent: NumberMask,
            inputProps: {
              onChange: handleChange,
              onBlur: handleBlur,
              value: localValue,
              validation,
            },
          }),
          startAdornment: field.customStartAdornment ? (
            <InputAdornment position="start" sx={customStartAdornment}>
              {field.customStartAdornment}
            </InputAdornment>
          ) : undefined,
        }}
        sx={
          [textFieldStyle(index === Object.entries(groupedFields).length - 1), sx] as SxProps<Theme>
        }
      />
    </>
  );
};

const customStartAdornment: SxProps<Theme> = ({ backrs }) => ({
  alignItems: 'center',
  paddingRight: backrs.spacing['spacing-md'],
  maxWidth: { xs: '50%', md: '175px' },
  width: { md: '175px' },
  borderRight: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  maxHeight: 'unset',
  height: backrs.spacing['spacing-5xl'],
  display: {
    xs: 'none',
    sm: 'flex',
  },
  '& > *': {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    width: '100%',
  },
});

const textFieldStyle =
  (isLastItem: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    mb: {
      xs: backrs.spacing['spacing-md'],
      sm: backrs.spacing[isLastItem ? 'spacing-md' : 'spacing-2xl'],
    },
    width: '100%',
    '& .MuiInputBase-adornedStart': {
      paddingY: `${backrs.spacing['spacing-none']} !important`,
    },
    '& .MuiInputBase-root': {
      height: '40px !important',
      minHeight: 'unset !important',
    },
    '& .MuiInputBase-input': {
      minWidth: '20px',
    },
    '& .MuiInputAdornment-root': {
      marginRight: 0,
      height: '40px !important',
      minHeight: 'unset !important',
      '& > *': {
        width: 'auto',
        height: 'auto',
      },
    },
  });

const labelSmallScreenSx: SxProps<Theme> = ({ backrs }) => ({
  display: {
    xs: 'block',
    sm: 'none',
  },
  my: {
    xs: backrs.spacing['spacing-xs'],
    sm: backrs.spacing['spacing-none'],
  },
});
